<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
    yz-card
      yz-dialog-fullscreen-header(:title="`Claim #${claimRid}`" no-actions, @back="onBack")
      yz-dialog-fullscreen-content.pa-6
        yz-card.pa-5(outlined)
          // Header section with total
          .d-flex.justify-space-between.align-center
            .yz-heading-6 Selected invoices
            .d-flex.align-center
              .yz-medium-body-sm.text-neutral-700 Total: {{ parentClaimedAmount | displayPrice($currency) }}
              yz-divider.mx-1(vertical)
              .yz-medium-body-xs.text-neutral-500 {{ totalCount }} {{ 'invoice' | pluralize({ count:totalCount }) }}
          // Data table
          yz-data-table.mt-3(:headers="invoicesHeaders" :items="invoicesData" :loading="isFetching")
            template(v-slot:item.visitDate="{ item }")
              .yz-regular-body-xs.font-medium {{ item.visitDate.date }}
              .yz-regular-body-xs {{ item.visitDate.time }}

            template(v-slot:item.invoiceNo="{ item }")
              .yz-medium-body-sm {{ item.invoiceNo }}

            template(v-slot:item.staffDetails="{ item }")
              .yz-medium-body-sm {{ item.staffName || 'N/A' }}
              .d-flex.align-center
                .yz-regular-body-xs.text-neutral-500(v-if="item.staffId") {{ item.staffId }}
                yz-badge.ml-1(v-if="item.staffBadge" border, border-color="#0C6DFF", radius="12px" text-color="#0C6DFF" height="16px")
                  span.yz-medium-body-2xs {{ item.staffBadge }}

            template(v-slot:item.patientDetails="{ item }")
              .py-2
                .yz-regular-body-xs.font-medium {{ item.patientName }}
                .yz-regular-body-xs(v-if="item.patientId") {{ item.patientId }}
                .yz-regular-body-xs Chit no. :&nbsp;
                  span(v-if="item.chitNo") {{ item.chitNo }}
                  span.text-neutral-500(v-else) N/A

            template(v-slot:item.mcDays="{ item }")
              .yz-medium-body-sm.text-center {{ item.mcDays }}

            template(v-slot:item.diagnosis="{ item }")
              .yz-medium-body-sm {{ item.diagnosis }}

            template(v-slot:item.treatments="{ item, index }")
              yz-tooltip(:direction="index === 0 ? 'bottom': 'right'" max-width="600px")
                yz-badge(border size="sm") {{ item.treatments }}
                template(#tooltip)
                  div.mb-3(v-for="(treatmentItems, categoryName) in item.treatmentDetails" :key="categoryName")
                    .yz-heading-9.text-white.mb-1 {{ categoryName }}
                    .yz-regular-body-sm.text-white(v-for="treatment in treatmentItems" :key="treatment") {{ treatment }}

            template(v-slot:item.total="{ item }")
              .yz-medium-body-sm {{ item.amount | displayPrice($currency) }}

            template(v-slot:item.actions="{ item }")
              .d-flex.align-center.justify-end.gap-2
                yz-btn(icon="$yzi-link-external-solid-01" size="sm" outlined background-color="white" icon-size="16px" @click="onViewInvoice(item)")
                yz-btn(icon="$yzi-trash-03" size="sm" outlined background-color="white" icon-color="danger" color="danger" icon-size="16px" @click="onDelete(item)")

            template(v-slot:footer)
              yz-divider.my-0
              // Pagination
              .py-2.px-3
                yz-pagination(v-model="currentPage" :page-size="pageSize" :count="totalCount" type="page" arrow, @input="onPageChanged")

    // Delete Invoice Bulk Claim Dialog
    invoice-bulk-claim-delete-confirmation-dialog(v-if="showDeleteInvoiceDialog" v-model="showDeleteInvoiceDialog" :parent-uid="invoiceToDelete.uid" @confirmed="onConfirmRemove" @canceled="invoiceToDelete = null")
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import InvoiceBulkClaimDeleteConfirmationDialog from '@/components/clinic/panel/dialogs/bulk/InvoiceBulkClaimDeleteConfirmationDialog.vue'

export default {
  components: { InvoiceBulkClaimDeleteConfirmationDialog },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    parentClaim: {
      type: Object,
      required: true
    },
    parentClaimedAmount: {
      type: Number,
      required: true
    },
    claimRid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      // UI State
      isFetching: true,
      isTableLoading: false,
      isError: false,

      // Dialog State
      showDeleteInvoiceDialog: false,
      invoiceToDelete: null,

      // Table Configuration
      invoicesHeaders: [
        { text: 'Visit date', value: 'visitDate', sortable: false },
        { text: 'Invoice no.', value: 'invoiceNo', sortable: false },
        { text: 'Staff details', value: 'staffDetails', sortable: false },
        { text: 'Patient details', value: 'patientDetails', sortable: false },
        { text: 'MC days', value: 'mcDays', sortable: false, align: 'center' },
        { text: 'Diagnosis', value: 'diagnosis', sortable: false },
        { text: 'Treatments', value: 'treatments', sortable: false },
        { text: 'Total', value: 'total', align: 'end', sortable: false },
        { text: '', value: 'actions', align: 'end', sortable: false }
      ],

      // Table Data
      invoicesData: [],

      // Pagination
      currentPage: 1,
      pageSize: 20,
      totalCount: 0
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    }
  },
  mounted() {
    if (this.parentClaim?.invoices && this.parentClaim.invoices.length > 0) {
      this.fetchAllInvoices()
    } else {
      this.isError = true
      this.isFetching = false
    }
  },
  watch: {
    'parentClaim.invoices': {
      handler(newInvoices, oldInvoices) {
        // Refetch invoices when the invoice list changes (e.g., after deletion)
        if (oldInvoices && newInvoices && newInvoices.length !== oldInvoices.length) {
          this.fetchAllInvoices()
        }
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    sanitizeData(invoice) {
      // Format visit date as separate date and time fields
      let visitDate = { date: '', time: '' }
      if (invoice.visit_date) {
        try {
          const dt = DateTime.fromISO(invoice.visit_date)
          visitDate = {
            date: dt.toFormat('dd MMMM yyyy'),
            time: dt.toFormat('h:mma').toLowerCase()
          }
        } catch (error) {
          console.error('Error formatting date:', error)
          visitDate = { date: invoice.visit_date, time: '' }
        }
      }

      // Group treatments by type
      const treatmentDetails = {}
      const treatmentTypes = {
        consultation: 'Consultation',
        service: 'Service',
        procedure: 'Procedure',
        medication: 'Medication',
        investigation: 'Investigation',
        document: 'Document',
        package: 'Package'
      }

      if (invoice.treatments && Array.isArray(invoice.treatments)) {
        invoice.treatments.forEach(treatment => {
          const categoryName = treatmentTypes[treatment.type] || 'Other'
          if (!treatmentDetails[categoryName]) {
            treatmentDetails[categoryName] = []
          }
          treatmentDetails[categoryName].push(treatment.name)
        })
      }

      // Format diagnosis - join array or take first item
      let diagnosis = ''
      if (invoice.diagnosis && Array.isArray(invoice.diagnosis) && invoice.diagnosis.length > 0) {
        diagnosis = invoice.diagnosis.join(', ')
      }

      return {
        uid: invoice.uid,
        visitDate,
        invoiceNo: invoice.invoice_no?.toString() || '',
        staffName: invoice.staff_name || '',
        staffId: invoice.staff_ic || '',
        staffBadge: invoice.relationship || '',
        patientName: invoice.name || '',
        patientId: invoice.ic || '',
        chitNo: invoice.chit_number || '',
        panelName: invoice.panel?.name || '',
        mcDays: invoice.mc_days || 0,
        diagnosis,
        treatments: `${invoice.treatments?.length || 0} items`,
        treatmentDetails,
        amount: invoice.amount || 0,
        originalData: invoice
      }
    },
    async fetchAllInvoices() {
      this.isFetching = true
      this.isError = false

      try {
        // Extract unique invoice UIDs from parentClaim.invoices
        const invoiceUids = this.parentClaim.invoices.join(',')

        // Get panel ID from parentClaim
        const panelId = this.parentClaim.panel?.id

        const params = {
          uids: invoiceUids,
          page_size: this.pageSize,
          page: this.currentPage,
          panel: panelId
        }

        const { data } = await this.$axios.get(`/panels/${this.company.slug}/invoices`, { params })

        this.totalCount = data.count
        // Transform and store the fetched invoice data
        if (data.count > 0) {
          this.invoicesData = data.results.map(invoice => this.sanitizeData(invoice))
        } else {
          this.isError = true
          console.error('No invoices found for the selected UIDs.')
          await this.showMessage(['error', 'No invoices found for the selected items. Please check and try again.'])
          this.invoicesData = []
        }
      } catch (error) {
        console.error('Error fetching invoices:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    onBack() {
      this.$emit('input', false)
    },
    onDelete(item) {
      this.invoiceToDelete = item.originalData
      this.showDeleteInvoiceDialog = true
    },
    onConfirmRemove() {
      // Check if we're going from 2 invoices to 1 (need to switch to single view)
      if (this.totalCount === 2) {
        this.$emit('switching-to-single')
        // Close the dialog since we'll be switching views
        this.$emit('input', false)
      } else {
        // Otherwise, just refresh the invoice table
        this.$emit('updated')
      }
      this.invoiceToDelete = null
    },
    onPageChanged() {
      this.fetchAllInvoices()
    },
    onViewInvoice(item) {
      const route = this.$router.resolve(`/admin/clinic/checkout/${item.originalData.lead_pid}`)
      window.open(route.href, '_blank')
    },
  }
}
</script>

<style scoped></style>
